import UserButton from '@/frontend/components/auth/UserButton'
import { useUser } from '@/frontend/contexts/AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
	faGavel, 
	faLandmarkDome, 
	faChartBar, 
	faFileAlt, 
	faHome, 
	faBullseye, 
	faChevronRight, 
	faUsers, 
	faChartLine, 
	faProjectDiagram, 
	faSync, 
	faReceipt,
	faUserCog,
	faUserPlus
} from '@fortawesome/free-solid-svg-icons'
import { type ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

// shadcn/ui components
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	useSidebar,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

interface LayoutProps {
	children: ReactNode
}

// Modern UserButton component
function ModernUserButton() {
	const { user, isLoaded } = useUser()

	if (!isLoaded) {
		return <Skeleton className="h-8 w-8 rounded-full" />
	}

	if (!user) {
		return null
	}

	return (
		<div className="flex items-center gap-3">
			<div className="flex flex-col text-sm">
				<span className="font-medium text-foreground">
					{user?.displayName || user?.email?.split('@')[0] || 'User'}
				</span>
				<span className="text-xs text-muted-foreground">
					{user?.email}
				</span>
			</div>
			<UserButton />
		</div>
	)
}

// App Sidebar component
function AppSidebar() {
	const location = useLocation()
	const { toggleSidebar, state } = useSidebar()

	// Check if any SLA route is active
	const isSLARouteActive = location.pathname.startsWith('/sla')
	const [slaExpanded, setSlaExpanded] = useState(isSLARouteActive)

	// Check if any Pay-in Slips route is active
	const isPayinRouteActive = location.pathname.startsWith('/payin-slips')
	const [payinExpanded, setPayinExpanded] = useState(isPayinRouteActive)

	// Check if any Admin route is active
	const isAdminRouteActive = location.pathname.startsWith('/admin')
	const [adminExpanded, setAdminExpanded] = useState(isAdminRouteActive)

	// Auto-expand SLA menu when on SLA routes
	useEffect(() => {
		if (isSLARouteActive && !slaExpanded) {
			setSlaExpanded(true)
		}
	}, [isSLARouteActive, slaExpanded])

	// Auto-expand Pay-in menu when on Pay-in routes
	useEffect(() => {
		if (isPayinRouteActive && !payinExpanded) {
			setPayinExpanded(true)
		}
	}, [isPayinRouteActive, payinExpanded])

	// Auto-expand Admin menu when on Admin routes
	useEffect(() => {
		if (isAdminRouteActive && !adminExpanded) {
			setAdminExpanded(true)
		}
	}, [isAdminRouteActive, adminExpanded])

	const menuItems = [
		{
			title: 'Dashboard',
			url: '/',
			icon: faHome,
		},
		{
			title: 'Documents',
			url: '/documents',
			icon: faFileAlt,
		},
		{
			title: 'Litigation',
			url: '/litigation',
			icon: faLandmarkDome,
		},
		{
			title: 'LED',
			url: '/led',
			icon: faGavel,
		},
		{
			title: 'Statistics',
			url: '/statistics',
			icon: faChartBar,
		},
	]

	// SLA sub-navigation items
	const slaSubItems = [
		{
			title: 'Dashboard',
			url: '/sla',
			icon: faHome,
		},
		{
			title: 'Cases',
			url: '/sla/cases',
			icon: faFileAlt,
		},
		{
			title: 'Assignees',
			url: '/sla/assignees',
			icon: faUsers,
		},
		{
			title: 'Reports',
			url: '/sla/reports',
			icon: faChartLine,
		},
		{
			title: 'Flows',
			url: '/sla/flows',
			icon: faProjectDiagram,
		},
		{
			title: 'Migration',
			url: '/sla/admin/migration',
			icon: faSync,
		},
	]

	// Pay-in Slips sub-navigation items
	const payinSubItems = [
		{
			title: 'Litigation',
			url: '/payin-slips/litigation/slips',
			icon: faLandmarkDome,
		},
		{
			title: 'LED',
			url: '/payin-slips/led/slips',
			icon: faGavel,
		},
	]

	// Admin sub-navigation items
	const adminSubItems = [
		{
			title: 'User Management',
			url: '/admin/users',
			icon: faUsers,
		},
		{
			title: 'User Invitations',
			url: '/admin/invitations',
			icon: faUserPlus,
		},
	]

	return (
		<div
			className={state === 'collapsed' ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}
			onClick={state === 'collapsed' ? toggleSidebar : undefined}
			title={state === 'collapsed' ? 'Click to expand sidebar' : undefined}
		>
			<Sidebar variant="inset" collapsible="icon">
				<SidebarHeader className="border-b border-sidebar-border">
					<div className="flex items-center gap-2 px-4 py-2 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
							<FontAwesomeIcon icon={faFileAlt} className="h-4 w-4" />
						</div>
						<div className="group-data-[collapsible=icon]:hidden flex flex-col">
							<span className="text-sm font-semibold">Document Processor</span>
							<span className="text-xs text-muted-foreground">Thai Court System</span>
						</div>
					</div>
				</SidebarHeader>
				<SidebarContent>
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								{/* Regular menu items */}
								{menuItems.map((item) => (
									<SidebarMenuItem key={item.title}>
										<SidebarMenuButton
											asChild
											isActive={location.pathname === item.url}
											className="w-full justify-start"
										>
											<Link to={item.url} className="flex items-center gap-3">
												<FontAwesomeIcon icon={item.icon} className="h-4 w-4" />
												<span>{item.title}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								))}

								{/* Pay-in Slips with expandable sub-menu */}
								<Collapsible
									open={payinExpanded}
									onOpenChange={setPayinExpanded}
									className="group/collapsible"
								>
									<SidebarMenuItem>
										<div className="flex items-center">
											<SidebarMenuButton
												asChild
												isActive={location.pathname === '/payin-slips'}
												className="flex-1"
											>
												<Link to="/payin-slips" className="flex items-center gap-3">
													<FontAwesomeIcon icon={faReceipt} className="h-4 w-4" />
													<span>Pay-in Slips</span>
												</Link>
											</SidebarMenuButton>
											<CollapsibleTrigger asChild>
												<SidebarMenuButton
													className="w-6 h-6 p-0 ml-1"
													isActive={isPayinRouteActive && location.pathname !== '/payin-slips'}
												>
													<FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
												</SidebarMenuButton>
											</CollapsibleTrigger>
										</div>
										<CollapsibleContent>
											<SidebarMenuSub>
												{payinSubItems.map((subItem) => (
													<SidebarMenuSubItem key={subItem.title}>
														<SidebarMenuSubButton
															asChild
															isActive={location.pathname === subItem.url}
														>
															<Link to={subItem.url} className="flex items-center gap-2">
																<FontAwesomeIcon icon={subItem.icon} className="h-3 w-3" />
																<span>{subItem.title}</span>
															</Link>
														</SidebarMenuSubButton>
													</SidebarMenuSubItem>
												))}
											</SidebarMenuSub>
										</CollapsibleContent>
									</SidebarMenuItem>
								</Collapsible>

								{/* SLA Tracking with expandable sub-menu */}
								<Collapsible
									open={slaExpanded}
									onOpenChange={setSlaExpanded}
									className="group/collapsible"
								>
									<SidebarMenuItem>
										<CollapsibleTrigger asChild>
											<SidebarMenuButton
												className="w-full justify-start"
												isActive={isSLARouteActive}
											>
												<div className="flex items-center gap-3 w-full">
													<FontAwesomeIcon icon={faBullseye} className="h-4 w-4" />
													<span>SLA Tracking</span>
													<FontAwesomeIcon icon={faChevronRight} className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
												</div>
											</SidebarMenuButton>
										</CollapsibleTrigger>
										<CollapsibleContent>
											<SidebarMenuSub>
												{slaSubItems.map((subItem) => (
													<SidebarMenuSubItem key={subItem.title}>
														<SidebarMenuSubButton
															asChild
															isActive={location.pathname === subItem.url}
														>
															<Link to={subItem.url} className="flex items-center gap-2">
																<FontAwesomeIcon icon={subItem.icon} className="h-3 w-3" />
																<span>{subItem.title}</span>
															</Link>
														</SidebarMenuSubButton>
													</SidebarMenuSubItem>
												))}
											</SidebarMenuSub>
										</CollapsibleContent>
									</SidebarMenuItem>
								</Collapsible>

								{/* Admin with expandable sub-menu */}
								<Collapsible
									open={adminExpanded}
									onOpenChange={setAdminExpanded}
									className="group/collapsible"
								>
									<SidebarMenuItem>
										<CollapsibleTrigger asChild>
											<SidebarMenuButton
												className="w-full justify-start"
												isActive={isAdminRouteActive}
											>
												<div className="flex items-center gap-3 w-full">
													<FontAwesomeIcon icon={faUserCog} className="h-4 w-4" />
													<span>Admin</span>
													<FontAwesomeIcon icon={faChevronRight} className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
												</div>
											</SidebarMenuButton>
										</CollapsibleTrigger>
										<CollapsibleContent>
											<SidebarMenuSub>
												{adminSubItems.map((subItem) => (
													<SidebarMenuSubItem key={subItem.title}>
														<SidebarMenuSubButton
															asChild
															isActive={location.pathname === subItem.url}
														>
															<Link to={subItem.url} className="flex items-center gap-2">
																<FontAwesomeIcon icon={subItem.icon} className="h-3 w-3" />
																<span>{subItem.title}</span>
															</Link>
														</SidebarMenuSubButton>
													</SidebarMenuSubItem>
												))}
											</SidebarMenuSub>
										</CollapsibleContent>
									</SidebarMenuItem>
								</Collapsible>
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</SidebarContent>
				<SidebarFooter className="border-t border-sidebar-border">
					<div className="p-4 group-data-[collapsible=icon]:hidden">
						<ModernUserButton />
					</div>
				</SidebarFooter>
			</Sidebar>
		</div>
	)
}

export default function LayoutNew({ children }: LayoutProps) {
	const location = useLocation()

	// Check if current path matches document review pattern
	const isDocumentReviewPage = /^\/task\/\d+\/document\/\d+$/.test(location.pathname)

	return (
		<SidebarProvider open={!isDocumentReviewPage}>
			<div className="flex min-h-screen w-full overflow-hidden">
				<AppSidebar />
				<div className="flex-1 flex flex-col min-w-0">
					{/* Main Content */}
					<main className="flex-1 overflow-auto p-4 sm:p-6">{children}</main>
				</div>
			</div>
		</SidebarProvider>
	)
}
